import React, { useState } from "react";
import { BaseModal } from "../../components/modalCommon/modalBase";
import { ModalHeader } from "../../components/modalCommon/modalHeader";
import { ModalBody } from "../../components/modalCommon/modalBody";
import { ModalFooter } from "../../components/modalCommon/modalFooter";
import { useI18n } from "../../utils/translate/i18n/useI18n";
import { useToast } from "../../components/toast/toast";
import { getApiUrl } from "../../utils/getApiUrl";
import { fetchWithAuth } from "../../auth/auth";
import { sendIcon } from "../../utils/config";
import { logActivity } from "../../utils/logActivity";
import { createResultFiles } from "../../utils/resultExport";

interface SendEmailResultModalProps {
  isOpen: boolean;
  onClose: () => void;
  captureResultPanel: (() => Promise<HTMLCanvasElement>) | null;
  fileBaseName: string;
  idtonalresult?: number | null;
  idverbalresult?: number | null;
  iduser?: number | null;
}

const SendEmailResultModal: React.FC<SendEmailResultModalProps> = ({
  isOpen,
  onClose,
  captureResultPanel,
  fileBaseName,
  idtonalresult,
}) => {
  const { t } = useI18n();
  const { showToast } = useToast();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const handleClose = () => {
    if (loading) return;
    setEmail("");
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  if (!captureResultPanel) return;

  setLoading(true);
  try {
    const canvas = await captureResultPanel();
    const { pngBase64, pdfBase64 } = createResultFiles(canvas);

    const res = await fetchWithAuth(getApiUrl("tools/send-result-email"), {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        recipientEmail: email.trim(),
        pngBase64,
        pdfBase64,
        fileName: fileBaseName,
      }),
    });

    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error((data as { error?: string }).error ?? `HTTP ${res.status}`);
    }

    logActivity('email_result', window.location.pathname, 'Wysłano na: ' + email.trim(), idtonalresult ?? null);
    showToast(t("Wyniki zostały wysłane na podany adres e-mail."), "info");
    setEmail("");
    onClose();
  } catch (err: unknown) {
    logActivity('email_result', window.location.pathname, 'Błąd wysyłki na: ' + email.trim(), idtonalresult ?? null);
    showToast(
      err instanceof Error ? err.message : t("Wystąpił błąd. Spróbuj ponownie."),
      "error"
    );
  } finally {
    setLoading(false);
  }
};

  return (
    <BaseModal open={isOpen} onClose={handleClose} size="md">
      <ModalHeader title={t("Wyślij wyniki e-mailem")} onClose={handleClose} />
      <form onSubmit={(e) => { void handleSubmit(e); }}>
        <ModalBody>
          <p className="mb-3 text-secondary">
            {t("Podaj adres e-mail, na który chcesz wysłać wyniki badania.")}
          </p>
          <div className="mb-3">
            <label htmlFor="send-result-email-input" className="form-label">
              {t("Adres e-mail")} <span className="text-danger">*</span>
            </label>
            <input
              id="send-result-email-input"
              type="email"
              className="form-control"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("Adres e-mail")}
              disabled={loading}
              required
              autoFocus
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={loading}
          >
            {t("Anuluj")}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={loading || email.trim().length === 0}
          >
            {loading ? t("Wysyłanie…") : <>{sendIcon} {t("Wyślij")}</>}
          </button>
        </ModalFooter>
      </form>
    </BaseModal>
  );
};

export default SendEmailResultModal;
